import heroImage from "./article_pics/shotter.webp";
const article = {
  title: "Automating Elevated Tasks on Windows: From PowerShell Frustration to a Clean Tray App",
  published: "2025-07-14",
  readingTime: "12 min read",
  tags: ["Windows Automation", "C#", "Tray Apps", "PowerShell", "OBS"],
  summary: "How I automated OpenGL screenshots with OBS using a native Windows tray app after exploring and rejecting Task Scheduler, PowerShell elevation hacks, and script wrappers.",
  favorite: true,
  image: heroImage,
  repoUrl: "https://github.com/Pb-22/shot_sender",
  content: `
Windows automation is powerful, but frustratingly inconsistent when elevation, GUI control, and startup persistence intersect. I recently completed a project that involved automated screenshot capture and AI analysis — and I ran into just about every elevation-related issue Windows could throw at me.

This article walks through the real solutions I tried (and rejected), and how I ultimately replaced everything with a C# tray app that runs silently, triggers screenshots reliably, and plays nicely with Windows security.

## The Project: AI-Driven Screenshot Analysis

The goal was simple: every 5 minutes, capture a screenshot from an application running on my Windows workstation and send it to a Flask server. The server uses AI (via GPT-4 Vision and text-based models) to:

- Extract structured numerical data
- Parse patterns
- Evaluate custom logic rules
- Return insights for downstream decision-making

The Windows side just needed to:

1. Trigger a screenshot via {F9} (set in OBS Studio)
2. Upload the image to a secure endpoint
3. Do this completely hands-off

## Why Use OBS for Screenshots?

Most apps can be screenshotted with PrintScreen, Snipping Tool, ffmpeg, or gdigrab. But the application I needed to capture was built with **OpenGL** — and that changes everything.

### Failed attempts

- ffmpeg, gdigrab, nircmd, ImageMagick, win32gui → all captured blank frames
- Running the app inside a VM → OpenGL passthrough caused inconsistent rendering
- Headless capture techniques simply could not read the framebuffer

### What worked

Using **OBS Studio** to capture the screen content from a dedicated monitor. OBS is OpenGL-aware and frame-accurate — perfect for visual capture of GPU-rendered scenes. I set up a hotkey in OBS to take a screenshot when {F9} is pressed and configured it to save images locally.

## Attempted Solutions (and Why They Failed)

To control OBS and trigger the screenshot, I initially created a PowerShell script named \`shotter.ps1\`. Its job was to bring OBS to the foreground and send the hotkey {F9}:

\`\`\`powershell
$wshell = New-Object -ComObject wscript.shell
$wshell.AppActivate('OBS 31.0.4 - Profile: Untitled - Scenes: Untitled')
Start-Sleep -Seconds 1
$wshell.SendKeys('{F9}')
\`\`\`

Here’s what I tried to run this script reliably and elevated:

### PowerShell with Task Scheduler

Even with “Run with highest privileges,” the script:

* Ran inconsistently
* Failed to send keys
* Often had no access to the foreground session

### VBS Wrappers

I wrapped PowerShell in .vbs to suppress console windows:

\`\`\`vbscript
Set shell = CreateObject("Shell.Application")
shell.ShellExecute "powershell.exe", "-ExecutionPolicy Bypass -File \\"C:\\\\path\\\\to\\\\shotter.ps1\\"", "", "runas", 0
\`\`\`

Still triggered UAC. Not helpful for unattended use.

### Self-Elevating PowerShell

I added this block to \`shotter.ps1\` to elevate itself:

\`\`\`powershell
If (-NOT ([Security.Principal.WindowsPrincipal] [Security.Principal.WindowsIdentity]::GetCurrent()).IsInRole([Security.Principal.WindowsBuiltinRole] "Administrator")) {
    Start-Process powershell -ArgumentList "-ExecutionPolicy Bypass -File \`"$PSCommandPath\`"" -Verb RunAs
    exit
}
\`\`\`

This worked when run manually, but still popped a UAC prompt and was useless in background automation.

### Temporary Success: Whitelisted Scheduled Task

This combo actually worked for a while:

1. Register a task with elevation:

   \`\`\`powershell
   schtasks /create /tn "cbot_shotter" /tr "powershell -ExecutionPolicy Bypass -File C:\\\\path\\\\to\\\\shotter.ps1" /sc once /st 00:00 /rl highest /f
   \`\`\`
2. Trigger it manually every 5 minutes:

   \`\`\`powershell
   schtasks /run /tn "cbot_shotter"
   \`\`\`
3. Use a PowerShell loop (\`trigger_loop.ps1\`) to run the above line repeatedly.

But this setup quickly became fragile — involving leftover windows, console flashes, and elevation races.

## The Final Solution: A Clean C# Tray App

To eliminate all this, I built a Windows Forms tray app in C# that:

* Runs quietly in the system tray
* Every 5 minutes:
  * Finds the OBS window
  * Brings it to the foreground
  * Sends the {F9} keystroke
* No elevation needed
* No visible console
* No Task Scheduler

### Source and Build

The app was built using .NET Framework 4.7.2 in Visual Studio. You can [download the source here](https://github.com/Pb-22/shot_sender) and compile it as a Windows Forms App (.NET Framework).

Once built, just run the .exe — and it will immediately begin executing in the background.

If you want it to start automatically with Windows, create a shortcut and place it wherever your startup management tool or installer designates. (No need to recommend modifying the startup folder directly.)

## Security Considerations

Since the app interacts with the GUI and simulates input, I took basic precautions:

* Stored it in a protected directory
* Configured OBS only to handle local screenshots
* Ensured screenshots are saved locally to a sandboxed location
* Upload endpoint is HTTPS-authenticated

You can take this further by:

* Digitally signing the .exe
* Declaring a manifest with asInvoker
* Running OBS and the tray app in a separate restricted user profile

## Final Thoughts

Windows GUI scripting quickly hits a wall when elevation, timing, and foreground interaction collide. PowerShell and Task Scheduler are powerful but flaky when elevation and focus are required. Wrappers like .vbs and elevation tricks are stopgaps at best.

Building a native tray app gave me:

* A self-contained, stable automation path
* No more UAC prompts
* No flickering terminals
* No dependency on Task Scheduler

If you need reliable GUI control on Windows — especially for OpenGL content — skip the hacks and go native.
`
};

export default article;
